import api from "@/utils/api";

export const callGetApi = async ({
  url,
  params = {},
  headers = undefined,
  // 프리뷰 토큰 등 고객 세션과 무관한 인증의 401 에서 axios 인터셉터의 refresh/logout 흐름을
  // 건너뛰는 옵션(config.skipAuthRefresh). NZSC-3650 이후 이 래퍼는 401 에 토큰을 지우지
  // 않으므로, 이 플래그의 역할은 인터셉터 우회 하나로 좁혀졌다.
  skipUnauthorizedCleanup = false,
  commit,
  errorMutation = null,
}) => {
  try {
    const res = await api.get(url, {
      params,
      ...(headers ? { headers } : {}),
      ...(skipUnauthorizedCleanup ? { skipAuthRefresh: true } : {}),
    });
    if (res?.data?.code !== 0) {
      handleError({
        error: { response: { data: res.data } },
        commit,
        errorMutation,
      });
      return null;
    }
    return res.data?.data ?? true;
  } catch (error) {
    handleError({ error, commit, errorMutation });
    return null;
  }
};

export const callPostApi = async ({ url, data = {}, commit, errorMutation = null }) => {
  try {
    const res = await api.post(url, data);
    if (res?.data?.code !== 0) {
      handleError({ error: { response: { data: res.data } }, commit, errorMutation });
      return null;
    }
    return res.data?.data ?? true;
  } catch (error) {
    handleError({ error, commit, errorMutation });
    return null;
  }
};

/**
 * POST 결과에서 HTTP status를 분기해야 하는 화면용 래퍼.
 * 기본 callPostApi의 null 계약은 유지하면서, 호출부가 423 같은 도메인 상태와
 * 네트워크 오류를 구분할 수 있게 secret-free 메타만 반환한다.
 */
export const callPostApiWithStatus = async ({ url, data = {}, commit, errorMutation = null }) => {
  try {
    const res = await api.post(url, data);
    if (res?.data?.code !== 0) {
      handleError({ error: { response: { data: res.data } }, commit, errorMutation });
      return { ok: false, status: res?.status || 0, data: null };
    }
    return { ok: true, status: res?.status || 200, data: res.data?.data ?? true };
  } catch (error) {
    handleError({ error, commit, errorMutation });
    return { ok: false, status: error?.response?.status || 0, data: null };
  }
};

export const callPatchApi = async ({ url, data = {}, commit, errorMutation = null }) => {
  try {
    const res = await api.patch(url, data);
    if (res?.data?.code !== 0) {
      handleError({ error: { response: { data: res.data } }, commit, errorMutation });
      return null;
    }
    return res.data?.data ?? true;
  } catch (error) {
    handleError({ error, commit, errorMutation });
    return null;
  }
};

/** PUT with an optional session fence for post-login preference migration. */
export const callPutApi = async ({ url, data = {}, expectedToken, timeout = 5000 }) => {
  try {
    const config = { timeout };
    if (expectedToken) {
      // A delayed optional write must not be refreshed/replayed under another account.
      config.skipAuthRefresh = true;
      config.transformRequest = [
        function (payload, headers) {
          if (window.localStorage.getItem("naezip_to_ac_se") !== expectedToken) {
            throw new Error("Preference session changed");
          }
          headers.Authorization = `Bearer ${expectedToken}`;
          headers["Content-Type"] = "application/json";
          return JSON.stringify(payload);
        },
      ];
    }
    const res = await api.put(url, data, config);
    return res?.data?.code === 0 ? res.data.data ?? null : null;
  } catch {
    return null;
  }
};

// data: DELETE 본문(예: NZSC-4281 모니터링 담보물 삭제 {seqs}). 주지 않으면 예전과 같이 params 만 보낸다.
export const callDeleteApi = async ({
  url,
  params = {},
  data = undefined,
  commit,
  errorMutation = null,
}) => {
  try {
    const res = await api.delete(url, { params, ...(data !== undefined ? { data } : {}) });
    if (res?.data?.code !== 0) {
      handleError({ error: { response: { data: res.data } }, commit, errorMutation });
      return null;
    }
    return res.data?.data ?? true;
  } catch (error) {
    handleError({ error, commit, errorMutation });
    return null;
  }
};

/**
 * multipart/form-data 전송용 POST 래퍼.
 * - 일반 POST 와 동일한 code===0 규약, 401 토큰 클린업, errorMutation 적용을 강제.
 * - 응답 전체 객체를 돌려준다 (`{ data, errorCode, message }` 등 추가 메타 접근 가능).
 *   단순 데이터만 필요하면 `data` 필드를 사용.
 * @returns {Promise<{ data: any, errorCode: string|null, message: string|null } | null>}
 */
export const callPostFormDataApi = async ({ url, formData, commit, errorMutation = null }) => {
  try {
    const res = await api.post(url, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    if (res?.data?.code !== 0) {
      handleError({ error: { response: { data: res.data } }, commit, errorMutation });
      return {
        data: res?.data?.data || null,
        errorCode: res?.data?.data?.errorCode || null,
        message: res?.data?.message || null,
        ok: false,
      };
    }
    return {
      data: res?.data?.data ?? true,
      errorCode: null,
      message: null,
      ok: true,
    };
  } catch (error) {
    handleError({ error, commit, errorMutation });
    // NestJS BadRequestException → axios catch. errorCode/message 전달.
    const errBody = error?.response?.data;
    if (errBody) {
      // Case A: NestJS wrap → { statusCode, message: { code, message, extra }, error }
      // Case B: 직접 envelope → { code, message, extra }
      const inner = errBody.message;
      const code = inner && typeof inner === "object" ? inner.message : inner;
      const extra = inner && typeof inner === "object" ? inner.extra : errBody.extra;
      return {
        data: extra || null,
        errorCode: code || null,
        message: code || null,
        ok: false,
      };
    }
    return null;
  }
};

/**
 * 파일(blob) 응답 POST 래퍼 — NZSC-4281 소유자 확인 필요 건 엑셀 내려받기.
 * 성공이면 { ok: true, blob, filename(Content-Disposition, 없으면 null) }.
 * 실패 본문은 blob 으로 오므로 JSON 으로 읽어 errorMutation 에 넣고 { ok: false } 를 돌려준다
 * (200 이어도 JSON envelope 면 code !== 0 실패로 본다 — 다른 래퍼와 같은 규약).
 */
export const callPostBlobApi = async ({ url, data = {}, commit, errorMutation = null }) => {
  const readJson = async blob => {
    try {
      return JSON.parse(await blob.text());
    } catch (e) {
      return null;
    }
  };
  try {
    const res = await api.post(url, data, { responseType: "blob" });
    const type = String(res?.headers?.["content-type"] || "");
    if (type.includes("application/json")) {
      const body = await readJson(res.data);
      if (body?.code !== 0) {
        handleError({ error: { response: { data: body } }, commit, errorMutation });
        return { ok: false };
      }
    }
    return {
      ok: true,
      blob: res.data,
      filename: fnParseDispositionFilename(res?.headers?.["content-disposition"]),
    };
  } catch (error) {
    const raw = error?.response?.data;
    const body = raw && typeof raw.text === "function" ? await readJson(raw) : raw;
    handleError({ error: { response: { data: body } }, commit, errorMutation });
    return { ok: false };
  }
};

/**
 * Content-Disposition 헤더의 파일명(NZSC-3120 체납·연체 PDF 에서 옮겨 와 함께 쓴다).
 * RFC 5987 filename*=UTF-8''{encoded} 우선(해석 실패 시 null), 없으면 filename="..."(해석 실패 시 원문).
 */
export function fnParseDispositionFilename(disposition) {
  if (!disposition) return null;
  const rfc5987 = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (rfc5987) {
    try {
      return decodeURIComponent(rfc5987[1]);
    } catch (e) {
      return null;
    }
  }
  const fallback = disposition.match(/filename="([^"]+)"/);
  if (fallback) {
    try {
      return decodeURIComponent(fallback[1]);
    } catch (e) {
      return fallback[1];
    }
  }
  return null;
}

// NZSC-3650: 401 이라고 해서 여기서 access token 을 지우지 않는다.
// 토큰 재발급은 api.js 응답 인터셉터가 이미 단독으로 담당한다. 그런데 /user/auth/refresh 는
// 호출마다 rotation + 이전 토큰 즉시 무효화이고 grace 가 없어(api-public auth.controller),
// 두 곳이 겹쳐 refresh 하면 늦은 쪽이 401 을 받는다 — 쿠키·세션은 멀쩡한데도. 여기서 그 401 에
// 반응해 토큰을 먼저 지워버리면 살아있는 세션이 죽고, 하필 그 찰나에 라우터 가드가 판정하면
// 홈으로 튕긴다(이용권은 이미 차감된 뒤 → order 413620 실측, 2026-08-21).
// 진짜 세션 만료는 인터셉터의 refresh 실패 경로가 로그아웃까지 처리한다.
const handleError = ({ error, commit, errorMutation }) => {
  if (commit && errorMutation && error?.response?.data) {
    commit(errorMutation, error.response.data);
  }
};
